import React, { useState, useEffect } from 'react';

const API_BASE = window.location.origin;

function AdminPanel({ controls = false }) {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [resetting, setResetting] = useState(false);
  const [notice, setNotice] = useState('');
  const [quizSettings, setQuizSettings] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({ dryRunStart: '', dryRunEnd: '', officialStart: '', officialEnd: '' });
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [scheduleFeedback, setScheduleFeedback] = useState(null);
  const [uploadingQuestions, setUploadingQuestions] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(true);
  const [teamsOpen, setTeamsOpen] = useState(true);

  useEffect(() => {
    const savedToken = localStorage.getItem('adminToken');
    if (savedToken) {
      setToken(savedToken);
      setIsLoggedIn(true);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn && token) {
      fetchUsers();
      if (controls) fetchQuizSettings();
    }
  }, [isLoggedIn, token, controls]);

  useEffect(() => {
    if (!isLoggedIn || !token) return undefined;
    const refreshUsers = () => fetchUsers({ background: true });
    const interval = window.setInterval(refreshUsers, 30_000);
    window.addEventListener('focus', refreshUsers);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshUsers);
    };
  }, [isLoggedIn, token]);

  async function handleLogin(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (data.success) {
        setToken(data.token);
        localStorage.setItem('adminToken', data.token);
        setIsLoggedIn(true);
      } else {
        setError(data.message || 'Login failed');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }

  async function fetchUsers({ background = false } = {}) {
    if (!background) setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` },
        cache: 'no-store'
      });

      if (response.status === 401) {
        handleLogout();
        return;
      }

      const data = await response.json();
      const fetchedUsers = data.users || [];
      setUsers(fetchedUsers);
      setTotalUsers(data.total || 0);
      if (controls) setSelectedIds(current => current.filter(id => fetchedUsers.some(user => user.id === id)));
    } catch (err) {
      setError('Failed to fetch users');
    } finally {
      if (!background) setLoading(false);
    }
  }

  async function fetchQuizSettings() {
    try {
      const response = await fetch(`${API_BASE}/api/admin/quiz-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.status === 401) return handleLogout();
      const data = await response.json();
      if (!response.ok) throw new Error(data.message);
      setQuizSettings(data);
      setScheduleForm(data.editorValues);
    } catch (err) {
      setError('Failed to load quiz settings');
    }
  }

  async function saveSchedule(event) {
    event.preventDefault();
    setSavingSchedule(true);
    setScheduleFeedback(null);
    try {
      const response = await fetch(`${API_BASE}/api/admin/quiz-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(scheduleForm)
      });
      if (response.status === 401) return handleLogout();
      const data = await response.json();
      if (!response.ok) throw new Error(data.message);
      setScheduleFeedback({ type: 'success', message: data.message || 'Schedule saved successfully.' });
      await fetchQuizSettings();
    } catch (err) {
      setScheduleFeedback({ type: 'error', message: err.message || 'Failed to save quiz schedule.' });
    } finally {
      setSavingSchedule(false);
    }
  }

  async function toggleRegistration() {
    if (!quizSettings) return;
    setSavingSchedule(true);
    setScheduleFeedback(null);
    try {
      const response = await fetch(`${API_BASE}/api/admin/registration-status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ registrationOpen: !quizSettings.registrationOpen })
      });
      if (response.status === 401) return handleLogout();
      const data = await response.json();
      if (!response.ok) throw new Error(data.message);
      setQuizSettings(current => ({ ...current, registrationOpen: data.registrationOpen }));
      setScheduleFeedback({ type: 'success', message: data.message });
    } catch (err) {
      setScheduleFeedback({ type: 'error', message: err.message || 'Failed to update registration status.' });
    } finally {
      setSavingSchedule(false);
    }
  }

  async function downloadQuestionTemplate() {
    try {
      const response = await fetch(`${API_BASE}/api/admin/official-questions/template`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.status === 401) return handleLogout();
      if (!response.ok) throw new Error('Failed to download template');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'napcon_official_quiz_questions_template.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Failed to download template');
    }
  }

  function uploadOfficialQuestions(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      setError('Upload the Excel (.xlsx) template.');
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      setUploadingQuestions(true);
      setError('');
      try {
        const workbookBase64 = String(reader.result).split(',')[1];
        const response = await fetch(`${API_BASE}/api/admin/official-questions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ workbookBase64 })
        });
        if (response.status === 401) return handleLogout();
        const data = await response.json();
        if (!response.ok) throw new Error(data.message);
        setNotice(`${data.message} (${data.count} questions)`);
        await fetchQuizSettings();
      } catch (err) {
        setError(err.message || 'Failed to upload official questions');
      } finally {
        setUploadingQuestions(false);
      }
    };
    reader.onerror = () => setError('Unable to read the selected file.');
    reader.readAsDataURL(file);
  }

  function handleLogout() {
    setToken('');
    setIsLoggedIn(false);
    localStorage.removeItem('adminToken');
  }

  async function exportToExcel() {
    try {
      const response = await fetch(`${API_BASE}/api/admin/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        handleLogout();
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'napcon_registrations.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export data');
    }
  }

  function toggleSelected(id) {
    setSelectedIds(current => (
      current.includes(id) ? current.filter(selected => selected !== id) : [...current, id]
    ));
  }

  function toggleSelectAll() {
    setSelectedIds(current => (current.length === users.length ? [] : users.map(user => user.id)));
  }

  function openDeleteDialog() {
    setError('');
    setNotice('');
    setDeleteConfirmText('');
    setDeleteDialogOpen(true);
  }

  function closeDeleteDialog() {
    setDeleteDialogOpen(false);
    setDeleteConfirmText('');
  }

  async function deleteSelected() {
    if (deleteConfirmText !== 'DELETE') return;

    setDeleting(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/admin/users/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ids: selectedIds, confirm: deleteConfirmText })
      });

      if (response.status === 401) {
        handleLogout();
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Failed to delete selected teams');
        return;
      }

      setNotice(`Deleted ${data.deletedCount} team${data.deletedCount === 1 ? '' : 's'}.`);
      setSelectedIds([]);
      closeDeleteDialog();
      await fetchUsers();
    } catch (err) {
      setError('Failed to delete selected teams');
    } finally {
      setDeleting(false);
    }
  }

  function openResetDialog(user, sessionType) {
    setError('');
    setNotice('');
    setResetConfirmText('');
    setResetTarget({ user, sessionType });
  }

  function closeResetDialog() {
    setResetTarget(null);
    setResetConfirmText('');
  }

  // Clears one attempt so the team can sign in again. The registration stays,
  // so no team has to be deleted and re-registered to get back in.
  async function resetAttempt() {
    if (!resetTarget || resetConfirmText !== 'RESET') return;

    setResetting(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/admin/users/reset-attempt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          id: resetTarget.user.id,
          sessionType: resetTarget.sessionType,
          confirm: resetConfirmText
        })
      });

      if (response.status === 401) {
        handleLogout();
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Failed to reset the attempt');
        return;
      }

      setNotice(data.message);
      closeResetDialog();
      await fetchUsers();
    } catch (err) {
      setError('Failed to reset the attempt');
    } finally {
      setResetting(false);
    }
  }

  function formatDate(dateString) {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  if (!isLoggedIn) {
    return (
      <div className="admin-login-container">
        <div className="admin-login-card">
          <div className="admin-login-header">
            <img src="/napcon_logo2.jpeg" alt="NAPCON Logo" className="admin-logo" />
            <h2>Admin Login</h2>
          </div>
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label>Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                required
              />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
            {error && <div className="error-message">{error}</div>}
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const dryRunAttemptedTeams = users.filter(u => u.hasDryRunAttempt);
  const onlineQuizSubmittedTeams = users.filter(u => u.hasOnlineQuizSubmission);
  const instituteSummary = Object.values(users.reduce((summary, user) => {
    const state = user.state || '-';
    const institute = user.collegeFullName || '-';
    const key = `${state}::${institute}`;

    if (!summary[key]) {
      summary[key] = { state, institute, totalTeams: 0 };
    }

    summary[key].totalTeams += 1;
    return summary;
  }, {})).sort((a, b) =>
    a.state.localeCompare(b.state) || a.institute.localeCompare(b.institute)
  );

  return (
    <div className="admin-container">
      <div className="admin-header">
        <div className="admin-header-left">
          <img src="/napcon_logo2.jpeg" alt="NAPCON Logo" className="admin-header-logo" />
          <div>
            <h1>NAPCON Admin Panel</h1>
            <p className="admin-subtitle">Team Registration Management</p>
          </div>
        </div>
        <div className="admin-actions">
          <a className="refresh-btn audit-link" href="/admin/control/audit">Answer audit</a>
          <a className="export-btn audit-link" href="/admin/control/leaderboard">Leaderboard</a>
          <button className="export-btn" onClick={exportToExcel}>
            Export to Excel (.xlsx)
          </button>
          <button className="refresh-btn" onClick={fetchUsers}>
            Refresh
          </button>
          <button className="logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      {controls && <section className="quiz-admin-section">
        <div className="registration-status-row">
          <div>
            <h2 className="section-header">Registration</h2>
            <p><strong>{quizSettings?.registrationOpen === false ? 'Closed' : 'Open'}</strong> — this takes effect immediately.</p>
          </div>
          <button
            className={quizSettings?.registrationOpen === false ? 'refresh-btn' : 'logout-btn'}
            type="button"
            onClick={toggleRegistration}
            disabled={!quizSettings || savingSchedule}
            aria-pressed={quizSettings?.registrationOpen !== false}
          >
            {!quizSettings ? 'Loading...' : savingSchedule ? 'Saving...' : quizSettings.registrationOpen === false ? 'Open Registration' : 'Close Registration'}
          </button>
        </div>
        <h2 className="section-header">Quiz Schedule <span>(IST)</span></h2>
        <p>Changes apply immediately and are used by both the landing page and quiz API.</p>
        <form className="quiz-schedule-form" onSubmit={saveSchedule}>
          <label>Dry Run starts<input type="datetime-local" value={scheduleForm.dryRunStart} onChange={(event) => setScheduleForm(current => ({ ...current, dryRunStart: event.target.value }))} required /></label>
          <label>Dry Run ends<input type="datetime-local" value={scheduleForm.dryRunEnd} onChange={(event) => setScheduleForm(current => ({ ...current, dryRunEnd: event.target.value }))} required /></label>
          <label>Online Quiz starts<input type="datetime-local" value={scheduleForm.officialStart} onChange={(event) => setScheduleForm(current => ({ ...current, officialStart: event.target.value }))} required /></label>
          <label>Online Quiz ends<input type="datetime-local" value={scheduleForm.officialEnd} onChange={(event) => setScheduleForm(current => ({ ...current, officialEnd: event.target.value }))} required /></label>
          <button className="refresh-btn" type="submit" disabled={savingSchedule}>{savingSchedule ? 'Saving...' : 'Save Schedule'}</button>
        </form>
        {scheduleFeedback && (
          <div className={`schedule-feedback schedule-feedback-${scheduleFeedback.type}`} role={scheduleFeedback.type === 'error' ? 'alert' : 'status'} aria-live="polite">
            {scheduleFeedback.message}
          </div>
        )}
        <h2 className="section-header">Official Questions</h2>
        <p>{quizSettings ? `${quizSettings.officialQuestions.count} of 30 questions uploaded${quizSettings.officialQuestions.uploadedAt ? ` — last upload ${formatDate(quizSettings.officialQuestions.uploadedAt)}` : ''}.` : 'Loading question-set status...'}</p>
        <div className="admin-actions question-upload-actions">
          <button className="export-btn" type="button" onClick={downloadQuestionTemplate}>Download Excel Template</button>
          <label className="refresh-btn file-upload-button">{uploadingQuestions ? 'Uploading...' : 'Upload Official Questions'}<input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={uploadOfficialQuestions} disabled={uploadingQuestions} /></label>
        </div>
      </section>}

      <div className="admin-stats">
        <div className="stat-card stat-total">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <h3>Total Teams</h3>
            <p>{totalUsers}</p>
          </div>
        </div>
        <div className="stat-card stat-completed">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <h3>Dry Run Attempted</h3>
            <p>{dryRunAttemptedTeams.length}</p>
          </div>
        </div>
        <div className="stat-card stat-pending">
          <div className="stat-icon">🏁</div>
          <div className="stat-content">
            <h3>Online Quiz Submitted</h3>
            <p>{onlineQuizSubmittedTeams.length}</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <div className="teams-container">
          <div className="collapsible-section-heading">
            <h2 className="section-header">Institute-wise Team Summary</h2>
            <button className="section-toggle" type="button" aria-expanded={summaryOpen} aria-controls="institute-summary" onClick={() => setSummaryOpen(open => !open)}>
              {summaryOpen ? 'Hide summary' : 'Show summary'}
            </button>
          </div>

          {summaryOpen && users.length > 0 && (
            <div className="table-scroll" id="institute-summary">
              <table className="institute-summary-table">
                <thead>
                  <tr>
                    <th>Institute State</th>
                    <th>Institute</th>
                    <th>Total No. of Teams</th>
                  </tr>
                </thead>
                <tbody>
                  {instituteSummary.map(({ state, institute, totalTeams }) => (
                    <tr key={`${state}-${institute}`}>
                      <td>{state}</td>
                      <td>{institute}</td>
                      <td>{totalTeams}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="2">Grand Total</td>
                    <td>{totalUsers}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          <div className="collapsible-section-heading">
            <h2 className="section-header">Registered Teams ({totalUsers})</h2>
            <button className="section-toggle" type="button" aria-expanded={teamsOpen} aria-controls="registered-teams" onClick={() => setTeamsOpen(open => !open)}>
              {teamsOpen ? 'Hide teams' : 'Show teams'}
            </button>
          </div>

          {teamsOpen && <div id="registered-teams">
            {notice && <div className="admin-notice">{notice}</div>}
            {error && <div className="error-message">{error}</div>}

          {controls && users.length > 0 && (
            <div className="selection-toolbar">
              <label className="select-all-label">
                <input
                  type="checkbox"
                  checked={selectedIds.length === users.length && users.length > 0}
                  onChange={toggleSelectAll}
                />
                Select all ({users.length})
              </label>
              <span className="selection-count">{selectedIds.length} selected</span>
              <button
                className="delete-selected-btn"
                onClick={openDeleteDialog}
                disabled={selectedIds.length === 0}
              >
                Delete Selected
              </button>
            </div>
          )}

          {users.length === 0 ? (
            <div className="no-data">No teams registered yet</div>
          ) : (
            <div className="teams-grid">
              {users.map((user, index) => (
                <div
                  key={user.id}
                  className={`team-card ${user.hasOnlineQuizSubmission ? 'completed' : user.hasDryRunAttempt ? 'attempted' : 'pending'} ${selectedIds.includes(user.id) ? 'selected' : ''}`}
                >
                  <div className="team-header">
                    {controls ? <label className="team-select">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(user.id)}
                        onChange={() => toggleSelected(user.id)}
                        aria-label={`Select team ${user.teamCode || user.teamNumber || index + 1}`}
                      />
                      <span className="team-number">{user.teamCode || `Team #${user.teamNumber ?? index + 1}`}</span>
                    </label> : <span className="team-number">{user.teamCode || `Team #${user.teamNumber ?? index + 1}`}</span>}
                    <div className="team-statuses">
                      <span className={`team-status ${user.hasDryRunAttempt ? 'status-completed' : 'status-pending'}`}>Dry Run: {user.hasDryRunAttempt ? 'Attempted' : 'Not attempted'}</span>
                      <span className={`team-status ${user.hasOnlineQuizSubmission ? 'status-completed' : 'status-pending'}`}>Online Quiz: {user.hasOnlineQuizSubmission ? 'Submitted' : 'Not attempted'}</span>
                    </div>
                  </div>

                  <div className="doctors-container">
                    <div className="doctor-card">
                      <div className="doctor-label">Doctor 1</div>
                      <div className="doctor-name">{user.doctor1Name || '-'}</div>
                      <div className="doctor-detail">
                        <span className="detail-label">Qualification:</span>
                        <span>{user.doctor1Qualification || '-'}</span>
                      </div>
                      <div className="doctor-detail">
                        <span className="detail-label">Email:</span>
                        <span>{user.doctor1Email || '-'}</span>
                      </div>
                      <div className="doctor-detail">
                        <span className="detail-label">Phone:</span>
                        <span>{user.doctor1PhoneNumber || '-'}</span>
                      </div>
                    </div>

                    <div className="doctor-card">
                      <div className="doctor-label">Doctor 2</div>
                      <div className="doctor-name">{user.doctor2Name || '-'}</div>
                      <div className="doctor-detail">
                        <span className="detail-label">Qualification:</span>
                        <span>{user.doctor2Qualification || '-'}</span>
                      </div>
                      <div className="doctor-detail">
                        <span className="detail-label">Email:</span>
                        <span>{user.doctor2Email || '-'}</span>
                      </div>
                      <div className="doctor-detail">
                        <span className="detail-label">Phone:</span>
                        <span>{user.doctor2PhoneNumber || '-'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="institute-section">
                    <div className="institute-label">Institute</div>
                    <div className="institute-name">{user.collegeFullName || '-'}</div>
                    <div className="institute-location">HOD: {user.hodName || '-'}</div>
                    {user.hodEmail && <div className="institute-location">HOD Email: {user.hodEmail}</div>}
                    {user.hodPhoneNumber && <div className="institute-location">HOD Contact: {user.hodPhoneNumber}</div>}
                    <div className="institute-location">
                      {user.city || '-'}, {user.state || '-'}
                    </div>
                  </div>

                  <div className="team-footer">
                    <span className="registration-date">
                      Registered: {formatDate(user.createdAt)}
                    </span>
                    {user.dryRunAttemptDate && <span className="quiz-date">Dry Run: {formatDate(user.dryRunAttemptDate)}</span>}
                    {user.onlineQuizSubmissionDate && <span className="quiz-date">Online Quiz: {formatDate(user.onlineQuizSubmissionDate)}</span>}
                  </div>

                  {controls && (user.hasDryRunAttempt || user.hasOnlineQuizSubmission) && (
                    <div className="reset-attempt-actions">
                      {user.hasDryRunAttempt && (
                        <button
                          className="reset-attempt-btn"
                          type="button"
                          onClick={() => openResetDialog(user, 'dry-run')}
                        >
                          Reset Dry Run
                        </button>
                      )}
                      {user.hasOnlineQuizSubmission && (
                        <button
                          className="reset-attempt-btn"
                          type="button"
                          onClick={() => openResetDialog(user, 'official')}
                        >
                          Reset Online Quiz
                        </button>
                      )}
                    </div>
                  )}

                  {controls && <div className="email-status-section">
                    <div className="email-status-heading">Email sending status</div>
                    <span className={`email-status ${user.confirmationEmailSentAt ? 'email-sent' : 'email-not-sent'}`}>
                      Registration confirmation: {user.confirmationEmailSentAt ? `Sent ${formatDate(user.confirmationEmailSentAt)}` : 'Not sent'}
                    </span>
                    <span className={`email-status ${user.whatsappGroupEmailSentAt ? 'email-sent' : 'email-not-sent'}`}>
                      WhatsApp group invitation: {user.whatsappGroupEmailSentAt ? `Sent ${formatDate(user.whatsappGroupEmailSentAt)}` : 'Not sent'}
                    </span>
                    <span className={`email-status ${user.quizDetailsEmailSentAt ? 'email-sent' : 'email-not-sent'}`}>
                      Quiz details: {user.quizDetailsEmailSentAt ? `Sent ${formatDate(user.quizDetailsEmailSentAt)}` : 'Not sent'}
                    </span>
                  </div>}
                </div>
              ))}
            </div>
          )}
          </div>}
        </div>
      )}

      {controls && deleteDialogOpen && (
        <div className="delete-dialog-overlay" role="dialog" aria-modal="true">
          <div className="delete-dialog">
            <h3>Delete {selectedIds.length} team{selectedIds.length === 1 ? '' : 's'}?</h3>
            <p className="delete-dialog-warning">
              This permanently removes the selected registrations, including participant
              details and any submitted scores. It cannot be undone.
            </p>

            <ul className="delete-dialog-list">
              {users
                .filter(user => selectedIds.includes(user.id))
                .map(user => (
                  <li key={user.id}>
                    {user.teamCode || `Team #${user.teamNumber ?? '-'}`} — {user.doctor1Name || '-'} &amp; {user.doctor2Name || '-'}
                  </li>
                ))}
            </ul>

            <label className="delete-confirm-label">
              Type <strong>DELETE</strong> to confirm
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                autoComplete="off"
                autoFocus
              />
            </label>

            <div className="delete-dialog-actions">
              <button className="dialog-cancel-btn" onClick={closeDeleteDialog} disabled={deleting}>
                Cancel
              </button>
              <button
                className="dialog-delete-btn"
                onClick={deleteSelected}
                disabled={deleteConfirmText !== 'DELETE' || deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {controls && resetTarget && (
        <div className="delete-dialog-overlay" role="dialog" aria-modal="true">
          <div className="delete-dialog">
            <h3>Reset {resetTarget.sessionType === 'official' ? 'Online Quiz' : 'Dry Run'} attempt?</h3>
            <p className="delete-dialog-warning">
              This clears the recorded score for{' '}
              <strong>{resetTarget.user.teamCode || `Team #${resetTarget.user.teamNumber ?? '-'}`}</strong>{' '}
              and removes any open quiz session, letting the team sign in again.
              The registration, team number and emails are left untouched.
              The cleared score cannot be recovered.
            </p>

            <label className="delete-confirm-label">
              Type <strong>RESET</strong> to confirm
              <input
                type="text"
                value={resetConfirmText}
                onChange={(e) => setResetConfirmText(e.target.value)}
                placeholder="RESET"
                autoComplete="off"
                autoFocus
              />
            </label>

            <div className="delete-dialog-actions">
              <button className="dialog-cancel-btn" onClick={closeResetDialog} disabled={resetting}>
                Cancel
              </button>
              <button
                className="dialog-delete-btn"
                onClick={resetAttempt}
                disabled={resetConfirmText !== 'RESET' || resetting}
              >
                {resetting ? 'Resetting...' : 'Reset Attempt'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPanel;
